import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Trash2, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { locationsAPI, bookingsAPI, eventsAPI, ownerBlocksAPI } from '../utils/api';

const CZ_MONTHS = [
  'Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen',
  'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec',
];
const CZ_DAYS = ['Po', 'Út', 'St', 'Čt', 'Pá', 'So', 'Ne'];

// Helpers
const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const inRange = (date, from, to) => {
  // All YYYY-MM-DD strings → lexicographic compare works
  const s = ymd(date);
  return s >= from && s <= to;
};

const OwnerCalendar = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [location, setLocation] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [events, setEvents] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [month, setMonth] = useState(() => {
    const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [selStart, setSelStart] = useState(null); // Date
  const [selEnd, setSelEnd] = useState(null);     // Date
  const [reason, setReason] = useState('');
  const [showDialog, setShowDialog] = useState(false);
  const [busy, setBusy] = useState(false);

  const fetchAll = async () => {
    try {
      const [loc, bk, ev, bl] = await Promise.all([
        locationsAPI.getById(id),
        bookingsAPI.getByLocation(id),
        eventsAPI.listByLocation(id),
        ownerBlocksAPI.list(id),
      ]);
      setLocation(loc.data);
      setBookings((bk.data || []).filter((b) => ['confirmed', 'pending', 'payment_pending'].includes(b.status)));
      setEvents(ev.data || []);
      setBlocks(bl.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Nepodařilo se načíst data');
      if (e.response?.status === 403) navigate('/dashboard');
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [id]);

  // Calendar grid days (incl. leading/trailing from prev/next month so we always have full weeks)
  const grid = useMemo(() => {
    const first = new Date(month.getFullYear(), month.getMonth(), 1);
    const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const startWeekday = (first.getDay() + 6) % 7; // Monday=0
    const days = [];
    for (let i = startWeekday; i > 0; i--) {
      days.push(new Date(month.getFullYear(), month.getMonth(), 1 - i));
    }
    for (let d = 1; d <= last.getDate(); d++) {
      days.push(new Date(month.getFullYear(), month.getMonth(), d));
    }
    while (days.length % 7 !== 0) {
      days.push(new Date(days[days.length - 1].getTime() + 86400000));
    }
    return days;
  }, [month]);

  const dayMeta = (d) => {
    const dateStr = ymd(d);
    const booking = bookings.find((b) => dateStr >= b.check_in && dateStr < b.check_out);
    const event = events.find((e) => dateStr >= e.start_date && dateStr <= e.end_date);
    const block = blocks.find((bl) => dateStr >= bl.date_from && dateStr <= bl.date_to);
    return { booking, event, block };
  };

  const today = new Date();
  const isPast = (d) => d < new Date(today.getFullYear(), today.getMonth(), today.getDate());

  const onDayClick = (d) => {
    if (isPast(d)) return;
    if (!selStart || (selStart && selEnd)) {
      setSelStart(d); setSelEnd(null);
    } else {
      if (d < selStart) { setSelEnd(selStart); setSelStart(d); }
      else setSelEnd(d);
    }
  };

  const openBlockDialog = () => {
    if (!selStart) return toast.error('Klikněte na první den blokace');
    setShowDialog(true);
  };

  const submitBlock = async () => {
    if (!selStart) return;
    const from = ymd(selStart);
    const to = ymd(selEnd || selStart);
    setBusy(true);
    try {
      await ownerBlocksAPI.create({
        location_id: id, date_from: from, date_to: to, reason: reason || null,
      });
      toast.success(`Termín ${from} – ${to} zablokován`);
      setSelStart(null); setSelEnd(null); setReason(''); setShowDialog(false);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Blokace selhala');
    } finally {
      setBusy(false);
    }
  };

  const removeBlock = async (block) => {
    if (!window.confirm(`Zrušit blokaci ${block.date_from} – ${block.date_to}?`)) return;
    try {
      await ownerBlocksAPI.remove(block.id);
      toast.success('Blokace zrušena');
      fetchAll();
    } catch (e) {
      toast.error('Smazání selhalo');
    }
  };

  const goPrev = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const goNext = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  const goToday = () => { const n = new Date(); setMonth(new Date(n.getFullYear(), n.getMonth(), 1)); };

  return (
    <div className="min-h-screen">
      <div
        className="relative bg-cover bg-center py-12 pb-24"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.65), rgba(0,0,0,0.65)), url('https://customer-assets.emergentagent.com/job_elementor-site-1/artifacts/ff4gwxo8_ChatGPT%20Image%2031.%203.%202026%2023_45_22.png')`,
        }}
      >
        <Header />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 sm:pt-44 lg:pt-52 pb-2 text-white">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-white hover:bg-white/10 mb-3 -ml-3"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Zpět na dashboard
          </Button>
          <h1 className="text-3xl sm:text-4xl font-bold mb-1">Kalendář vašeho místa</h1>
          {location && <p className="text-white/80">{location.name}</p>}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-12 pb-12 space-y-6 relative z-10">
        <Card className="shadow-xl">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={goPrev} data-testid="cal-prev">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <h2 className="text-xl sm:text-2xl font-bold min-w-[180px] text-center" data-testid="cal-month">
                  {CZ_MONTHS[month.getMonth()]} {month.getFullYear()}
                </h2>
                <Button variant="outline" size="icon" onClick={goNext} data-testid="cal-next">
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button variant="ghost" onClick={goToday} className="text-sm" data-testid="cal-today">Dnes</Button>
              </div>
              <Button
                onClick={openBlockDialog}
                disabled={!selStart}
                className="bg-orange-600 hover:bg-orange-700"
                data-testid="cal-add-block"
              >
                <Plus className="w-4 h-4 mr-1" />
                Blokovat vybraný termín
              </Button>
            </div>

            {/* Legend */}
            <div className="flex flex-wrap gap-3 text-xs text-gray-600 mb-3">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-500"></span> rezervace</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-purple-500"></span> akce</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-500"></span> vlastní blokace</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm border-2 border-orange-500"></span> vybráno</span>
            </div>

            <div className="grid grid-cols-7 gap-1 text-xs font-semibold text-gray-500 mb-1">
              {CZ_DAYS.map((d) => <div key={d} className="text-center py-1">{d}</div>)}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {grid.map((d, i) => {
                const isThisMonth = d.getMonth() === month.getMonth();
                const meta = dayMeta(d);
                const past = isPast(d);
                const isSelected = selStart && (
                  (selEnd ? d >= selStart && d <= selEnd : ymd(d) === ymd(selStart))
                );
                const cellClass = [
                  'relative aspect-square sm:h-20 sm:aspect-auto p-1 rounded border text-xs flex flex-col',
                  isThisMonth ? 'bg-white' : 'bg-gray-50 text-gray-400',
                  past ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-orange-50',
                  isSelected ? 'ring-2 ring-orange-500 z-10' : '',
                ].join(' ');
                return (
                  <button
                    key={i}
                    type="button"
                    disabled={past}
                    onClick={() => onDayClick(d)}
                    className={cellClass}
                    data-testid={`cal-day-${ymd(d)}`}
                  >
                    <span className={`text-right block ${d.toDateString() === today.toDateString() ? 'font-bold text-orange-600' : ''}`}>{d.getDate()}</span>
                    <div className="flex flex-col gap-0.5 mt-auto items-stretch">
                      {meta.booking && (
                        <span className="bg-green-500 text-white rounded px-1 py-0.5 text-[10px] leading-tight truncate" title={`Rezervace: ${meta.booking.user?.name || ''}`}>
                          ●
                        </span>
                      )}
                      {meta.event && (
                        <span className="bg-purple-500 text-white rounded px-1 py-0.5 text-[10px] leading-tight truncate" title={`Akce: ${meta.event.name}`}>
                          ◆
                        </span>
                      )}
                      {meta.block && (
                        <span className="bg-red-500 text-white rounded px-1 py-0.5 text-[10px] leading-tight truncate" title={meta.block.reason || 'Blokováno'}>
                          ✕
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {selStart && (
              <p className="text-sm text-gray-700 mt-3" data-testid="cal-selection-info">
                Vybráno: <strong>{ymd(selStart)}</strong>
                {selEnd && ymd(selEnd) !== ymd(selStart) && <> – <strong>{ymd(selEnd)}</strong></>}
                <Button variant="ghost" size="sm" onClick={() => { setSelStart(null); setSelEnd(null); }} className="ml-2 text-gray-500">Zrušit výběr</Button>
              </p>
            )}
          </CardContent>
        </Card>

        {/* Existing blocks list */}
        <Card>
          <CardContent className="p-6">
            <h3 className="font-bold text-lg mb-3">Moje aktivní blokace</h3>
            {blocks.length === 0 ? (
              <p className="text-gray-500 text-sm">Žádné blokace zatím nemáte.</p>
            ) : (
              <ul className="space-y-2">
                {blocks.map((b) => (
                  <li key={b.id} className="flex items-center justify-between bg-red-50 border border-red-200 rounded px-3 py-2" data-testid={`cal-block-${b.id}`}>
                    <div className="text-sm">
                      <span className="font-medium">{b.date_from} → {b.date_to}</span>
                      {b.reason && <span className="text-gray-600 ml-2">— {b.reason}</span>}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBlock(b)}
                      className="text-red-600 hover:bg-red-100"
                      data-testid={`cal-remove-${b.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Zrušit
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Blokovat termín</DialogTitle>
            <DialogDescription>
              {selStart && (
                <>
                  Od <strong>{ymd(selStart)}</strong>
                  {' '}do <strong>{ymd(selEnd || selStart)}</strong>
                </>
              )}
              <br />
              V tomto termínu nebudou možné nové rezervace od hostů.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="block-reason">Důvod (volitelné, jen pro vás)</Label>
            <Input
              id="block-reason"
              placeholder="Např. údržba pozemku, soukromá akce"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="cal-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Zrušit</Button>
            <Button onClick={submitBlock} disabled={busy} className="bg-orange-600 hover:bg-orange-700" data-testid="cal-confirm-block">
              {busy ? 'Ukládám…' : 'Blokovat termín'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default OwnerCalendar;
